import React, {
  useEffect,
  useState,
  useCallback,
  useLayoutEffect,
  useRef,
  useMemo,
} from "react";
import { useSelector, useDispatch } from "react-redux";

import ScrollToLastMessage from "./ScrollToLastMessage";
import RequestAccess from "./RequestAccess";
import LoadingState from "src/components/LoadingState";
import usePrevious from "src/hooks/usePrevious";
import TypingIndicator from "src/components/chatroom/TypingIndicator";
import { currentRoomAccessStatuses } from "src/reducers/chatRooms";
import {
  getFilteredMessages,
  getCurrentUserId,
  getMessageCount,
  getChatroomPrivacy,
  getChatroomMembership,
  isProcessOwner as getIsProcessOwner,
  getWorkflowWhitelistMembership,
  getConversationModalVisibility,
  getConversationModalId,
  getIsSrwMobile,
  getCurrentRoomAccess,
  getCurrentRoomId,
  getFilteredRecentChats,
} from "src/reducers";
import { resyncMessages } from "src/actions/chatroom";

import type { ReactRef, State } from "src/types";
import type { Nullable } from "src/ts-types";

import { ChatHistory as StyledChatHistory } from "./styles";
import RecentChats from "./RecentChats";

const History = () => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);

  const currentRoomId = useSelector(({ app }: State) => getCurrentRoomId(app));
  const conversationModalRoomId = useSelector(({ app }: State) =>
    getConversationModalId(app),
  );
  const modal = useSelector(({ app }: State) =>
    getConversationModalVisibility(app),
  );

  // Use the roomID of the conversation modal if it's open
  // otherwise use the current room ID from the redux store
  const roomId = (modal ? conversationModalRoomId : currentRoomId) ?? "";

  const currentRoomAccessStatus = useSelector(({ app }: State) =>
    getCurrentRoomAccess(app),
  );
  const recentChats = useSelector(({ app }: State) =>
    getFilteredMessages(app, roomId),
  );
  const filteredChats = useSelector(({ app }: State) =>
    getFilteredRecentChats(app, roomId),
  );

  const currentUser = useSelector(({ app }: State) => getCurrentUserId(app));
  const totalCount = useSelector(({ app }: State) =>
    getMessageCount(app, roomId),
  );
  const privacy = useSelector(({ app }: State) =>
    getChatroomPrivacy(app, roomId),
  );
  const membership = useSelector(({ app }: State) =>
    getChatroomMembership(app, roomId),
  );
  const isProcessOwner = useSelector(({ app }: State) =>
    getIsProcessOwner(app, roomId),
  );

  const whitelistMembership = useSelector(({ app }: State) =>
    getWorkflowWhitelistMembership(app),
  );

  const isMobile = useSelector(({ app }: State) => getIsSrwMobile(app));

  const messagesEnd: ReactRef = useRef<HTMLDivElement>(null);
  const messageMarker: ReactRef = useRef<HTMLDivElement>(null);

  const [lastMessage, setLastMessage] = useState<Nullable<string>>(null);

  const prevRecentChats = usePrevious(recentChats);
  const prevRoomId = usePrevious(roomId);

  const scrollToBottom = useCallback(() => {
    if (messagesEnd.current) {
      messagesEnd.current.scrollIntoView({ behavior: "instant" });
    }
  }, []);

  useEffect(() => {
    if (roomId) {
      scrollToBottom();
      setLastMessage(null);
    }
  }, [prevRoomId, roomId, modal]);

  // if recentChats just loaded for this chatroom
  // (lastMessage is null at this moment) then scroll to bottom
  // or if one message got added because of current user message or action
  // scroll to bottom (ensure one message didnt get added because of loadMore)
  useLayoutEffect(() => {
    if (
      lastMessage === null ||
      (prevRecentChats &&
        recentChats.length - prevRecentChats.length === 1 &&
        recentChats.length < totalCount &&
        recentChats?.[recentChats.length - 1]?.author === currentUser)
    ) {
      scrollToBottom();
    }
    // else when recentChats load during loadMore, scroll to last seen message
    else if (messageMarker.current && lastMessage !== recentChats[0]?.id)
      messageMarker.current.scrollIntoView({
        behavior: "instant",
        block: "start",
      });
    // update lastSeenMessage optimistically to top most message in recentChats
    if (recentChats.length > 0) setLastMessage(recentChats[0]?.id ?? null);
  }, [JSON.stringify(recentChats)]);

  const loadMore = useCallback(() => {
    dispatch(resyncMessages(roomId, recentChats.length + 100, modal));
  }, [JSON.stringify(recentChats), roomId, totalCount, modal]);

  const handleScroll = useCallback(
    (e: any) => {
      e.preventDefault();
      const reachedTop = e.target.scrollTop === 0;
      if (reachedTop) loadMore();
    },
    [loadMore],
  );

  const renderMessageMarker = useCallback(
    () => <div ref={messageMarker} />,
    [],
  );

  const memoizedFilteredChats = useMemo(() => {
    return [...filteredChats];
  }, [JSON.stringify(filteredChats)]);

  if (
    privacy === "content" &&
    !membership &&
    !isProcessOwner &&
    !whitelistMembership
  )
    return <RequestAccess />;

  if (
    (recentChats.length === 0 && totalCount && totalCount !== 0) ||
    currentRoomAccessStatus === null ||
    currentRoomAccessStatus === currentRoomAccessStatuses.restricted
  ) {
    return <LoadingState type={isMobile ? "mobileHistory" : "history"} />;
  }

  return (
    <StyledChatHistory onScroll={handleScroll} ref={containerRef}>
      <ScrollToLastMessage scrollToBottom={scrollToBottom} />
      <RecentChats
        historyRef={containerRef}
        roomId={roomId}
        chats={memoizedFilteredChats}
        renderMessageMarker={renderMessageMarker}
        lastMessage={lastMessage}
        messagesEnd={messagesEnd}
      />
      <TypingIndicator roomId={roomId} />
    </StyledChatHistory>
  );
};

export default History;
